import { MoCommand } from "./command";

// EPSILON constant (also defined on global in app.ts)
const EPSILON = 0.0001;

/**
 * ActorObject command for handling moving to a position.
 *
 * @event enter When this command enters.
 * @event leave When this command leaves.
 * @event finish When we reach our target position.
 *
 * @param object
 * @param pos
 * @constructor
 */
export class MoveToPosCmd extends MoCommand {
    targetPos: any;

    constructor(object: any, pos: any) {
        super(object);
        this.targetPos = pos;
    }

    enter() {
        let thisPos = this.object.position;
        let targetPos = this.targetPos;

        const deltaPos = new THREE.Vector2(
            targetPos.x - thisPos.x,
            targetPos.y - thisPos.y
        );
        this.object.direction =
            Math.atan2(deltaPos.y, deltaPos.x) + Math.PI / 2;

        this.object.pawn.playRunMotion();
    }

    leave() {
        if (!this.object.nextCmd) {
            this.object.pawn.playIdleMotion();
        }
    }

    update(delta: number) {
        if (this.wantInterrupt) {
            this.isComplete = true;
            return delta;
        }

        const thisObj = this.object;
        const thisPos = thisObj.position;
        const targetPos = this.targetPos;
        let deltaLeft = 0;

        const frameMoveSpeed = thisObj.moveSpeed * 0.01 * delta;

        const velocity = new THREE.Vector2(
            targetPos.x - thisPos.x,
            targetPos.y - thisPos.y
        );
        if (velocity.lengthSq() > frameMoveSpeed * frameMoveSpeed) {
            velocity.normalize();
            velocity.multiplyScalar(frameMoveSpeed);
        } else {
            deltaLeft = delta * (1 - velocity.length() / frameMoveSpeed);
        }

        if (velocity.lengthSq() > EPSILON) {
            this.object.direction =
                Math.atan2(velocity.y, velocity.x) + Math.PI / 2;

            const newPosition = thisPos.clone();
            newPosition.x += velocity.x;
            newPosition.y += velocity.y;

            const highPointInfo = thisObj.world.findHighPointWithInfo(
                newPosition.x,
                newPosition.y,
                newPosition.z + 1
            );

            const highZ = highPointInfo ? highPointInfo.z : undefined;
            const isTerrain = highPointInfo ? highPointInfo.isTerrain : false;

            // Different thresholds for terrain vs objects
            const terrainMaxSlopeRatio = 1.3; // Slope for terrain (blocks very steep hills)
            const objectMaxStepHeight = 0.5; // Allow stepping on object stairs
            const objectSlopeRatio = 5.0; // Loose slope for object stairs
            const gravity = 10; // Gravity for falling (units per second)

            // Calculate height difference
            const heightDiff = highZ !== undefined ? highZ - thisPos.z : 0;

            // Calculate horizontal movement distance
            const horizontalDist = Math.sqrt(
                velocity.x * velocity.x + velocity.y * velocity.y
            );

            // Check slope ratio
            const slopeRatio =
                horizontalDist > 0.001 ? heightDiff / horizontalDist : 0;

            // Apply different rules for terrain vs objects
            let canClimb = false;
            if (heightDiff <= 0) {
                // Going down is always allowed
                canClimb = true;
            } else if (isTerrain) {
                // Terrain: strict slope check only
                canClimb = slopeRatio <= terrainMaxSlopeRatio;
            } else {
                // Objects (stairs, etc.): loose height and slope check
                canClimb =
                    heightDiff <= objectMaxStepHeight &&
                    slopeRatio <= objectSlopeRatio;
            }

            if (
                !thisObj.useMoveCollision ||
                (highZ !== undefined && canClimb)
            ) {
                // Apply position with gravity for falling
                if (heightDiff < 0) {
                    // Going down - apply weak gravity (gradual fall)
                    const fallAmount = Math.min(
                        Math.abs(heightDiff),
                        gravity * delta
                    );
                    newPosition.z = thisPos.z - fallAmount;
                } else {
                    // Going up or level - snap to ground
                    newPosition.z = highZ;
                }

                // Check horizontal collision with objects (walls, etc.)
                let canMove = true;
                if (
                    thisObj.useMoveCollision &&
                    thisObj.world.collisionDetector
                ) {
                    canMove = thisObj.world.collisionDetector.checkCollision(
                        thisObj,
                        newPosition
                    );
                }

                if (canMove) {
                    thisObj.setPosition(
                        newPosition.x,
                        newPosition.y,
                        newPosition.z
                    );

                    // Check if we have reached our target?
                    const targetX = this.targetPos.x - thisObj.position.x;
                    const targetY = this.targetPos.y - thisObj.position.y;
                    if (targetX * targetX + targetY * targetY <= EPSILON) {
                        this.isComplete = true;
                        this.emit("finish");
                    }

                    return deltaLeft;
                } else {
                    // Blocked by collision object
                    this.isComplete = true;
                    return delta;
                }
            } else {
                // Bumped into something or invalid position, just stop for now
                this.isComplete = true;
                return delta;
            }
        } else {
            this.isComplete = true;
            this.emit("finish");
            return delta;
        }
    }
}
